import React, { useState, useEffect, useRef } from 'react';
import ChatBot from './ChatBot';

const ChatBotToggle = () => {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const supportTimerRef = useRef(null);
  const supportDialogRef = useRef(null);

  const toggleChatbot = () => {
    setIsChatbotOpen(!isChatbotOpen);
    setShowSupportDialog(false); // Hide support dialog when chatbot is toggled
    resetSupportTimer();
  };

  // Set up support message timer
  useEffect(() => {
    // Only start timer if chatbot is not open
    if (!isChatbotOpen) {
      resetSupportTimer();
    } else {
      // Clear timer when chatbot is open
      if (supportTimerRef.current) {
        clearTimeout(supportTimerRef.current);
      }
    }

    return () => {
      if (supportTimerRef.current) {
        clearTimeout(supportTimerRef.current);
      }
    };
  }, [isChatbotOpen]);

  const resetSupportTimer = () => {
    // Clear any existing timer
    if (supportTimerRef.current) {
      clearTimeout(supportTimerRef.current);
    }

    // Start a new timer for support message
    supportTimerRef.current = setTimeout(() => {
      if (!isChatbotOpen) {
        setShowSupportDialog(true);
      }
    }, 10000 * 60 * 10); // Show after 10 minutes of inactivity
  };

  // Handle click outside the support dialog
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSupportDialog && 
        supportDialogRef.current && 
        !supportDialogRef.current.contains(event.target)
      ) {
        setShowSupportDialog(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSupportDialog]);

  // Only reset timer on user activity if dialog isn't shown yet
  useEffect(() => {
    const handleUserActivity = () => {
      // Only reset timer if support dialog is not already shown
      if (!showSupportDialog) {
        resetSupportTimer();
      }
    };

    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('mousemove', handleUserActivity);
    
    return () => {
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('mousemove', handleUserActivity);
    };
  }, [showSupportDialog]);

  return (
    <>
      {/* Support dialog */}
      {showSupportDialog && (
        <div 
          ref={supportDialogRef}
          className="fixed font-spaceGrotesk bottom-24 right-5 z-50 bg-primary text-black p-4 rounded-lg shadow-lg max-w-xs animate-fade-in"
        >
          <div className="flex items-start">
            <div className="flex-grow">
              <p className="font-semibold text-sm mb-1">Having any trouble?</p>
              <p className="text-sm">Contact us on <a href="tel:+919769124727" className="text-white font-bold">+91 97691 24727</a> for immediate assistance.</p>
            </div>
            <button 
              className="ml-2 text-white hover:text-gray-700"
              onClick={() => setShowSupportDialog(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Floating button to toggle chatbot */}
      <button
        onClick={toggleChatbot}
        className="fixed bottom-5 right-5 z-50 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        aria-label="Toggle chat assistant"
      >
        {isChatbotOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Chatbot component */}
      <ChatBot isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />

      {/* Add styles for fade-in animation */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default ChatBotToggle; 